<template>
  <div class="flex flex-col h-full overflow-hidden">
    <!-- Library list (always visible) -->
    <div class="flex items-center justify-between px-3 py-2 border-b border-slate-600 flex-shrink-0">
      <span class="text-xs font-semibold text-slate-300 uppercase tracking-wide">Font Library</span>
      <button
        class="text-cyan-400 hover:text-cyan-300 text-xs font-medium px-2 py-1 rounded hover:bg-slate-600 transition-colors"
        title="Create font"
        @click="createFont"
      >+ New</button>
    </div>

    <ResourceListToolbar
      v-if="fonts.length > 0"
      :search="search"
      @update:search="search = $event"
      :sortKey="sortKey"
      @update:sortKey="sortKey = $event"
      :sortOptions="sortOptions"
    />

    <div class="flex-1 overflow-y-auto">
      <div v-if="fonts.length === 0" class="px-3 py-6 text-xs text-slate-500 text-center">
        No fonts yet.<br>
        Click <strong class="text-slate-400">+ New</strong> to create one.
      </div>
      <div v-else-if="filteredFonts.length === 0" class="px-3 py-6 text-xs text-slate-500 text-center">
        No fonts match your search.
      </div>

      <ResourceListItem
        v-for="f in filteredFonts"
        :key="f.uuid"
        :title="f.name"
        :meta="rowMeta(f)"
        :warnings="getWarnings(f)"
        :used="isUsed(f)"
        @select="openEditor(f.uuid)"
      >
        <template #actions>
          <button
            class="text-slate-400 hover:text-cyan-300 p-1 rounded hover:bg-slate-600 transition-colors"
            title="Duplicate"
            @click="duplicateFont(f.uuid)"
          >
            <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
          </button>
          <button
            class="text-slate-400 hover:text-red-400 p-1 rounded hover:bg-slate-600 transition-colors"
            title="Delete"
            @click="pendingDeleteUUID = f.uuid"
          >
            <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        </template>
      </ResourceListItem>
    </div>
  </div>

  <ConfirmationModal
    :show="pendingDeleteUUID !== null"
    title="Delete Font"
    :message="pendingDeleteFont ? `Are you sure you want to delete &lt;strong&gt;${pendingDeleteFont.name}&lt;/strong&gt;?` : 'Are you sure?'"
    @confirm="confirmDelete"
    @cancel="pendingDeleteUUID = null"
  />

  <!-- Font editor — draggable floating popup, mirrors DatasetManager's editor popup -->
  <FloatingPopup
    id="font-editor"
    :modelValue="editingUUID !== null && editingFont !== null"
    @update:modelValue="v => { if (!v) editingUUID = null }"
    :title="editingFont?.name || 'Font'"
    :width="420"
    :height="520"
    :minwidth="360"
    :minheight="380"
    :position="{ x: 300, y: 80 }"
  >
    <FontEditor
      v-if="editingFont"
      :font="editingFont"
      :objectlayer="objectlayer"
      @navigate-to-widget="$emit('navigate-to-widget', $event)"
    />
  </FloatingPopup>
</template>

<script setup>
/**
 * FontManager.vue — Project Resource architecture (see
 * docs/PROJECT-RESOURCE-ARCHITECTURE-PLAN.md). Modeled directly on
 * DatasetManager.vue's structure: list / create / duplicate / delete here,
 * everything else (rename, id/file/size, "used by") in FontEditor.vue via
 * the same FloatingPopup-hosted-editor pattern DatasetManager/DatasetEditor
 * already use.
 *
 * A widget's Font Family selection lives on its `font` property, whose value
 * is the exact wire-format string (see label.js) — for a project font, that's
 * this resource's `file` path — so usage lookups match `font` against `file`,
 * not `uuid`. `text_font` is a different property (the pixel size).
 */
import { ref, computed, watch } from 'vue'
import { useFontStore } from '../../stores/fontStore.js'
import { findWidgetsReferencing } from '../../utils/resourceUsage.js'
import FontEditor from './FontEditor.vue'
import FloatingPopup from '../FloatingPopup.vue'
import ConfirmationModal from '../ConfirmationModal.vue'
import ResourceListToolbar from '../resources/ResourceListToolbar.vue'
import ResourceListItem from '../resources/ResourceListItem.vue'

const props = defineProps({
  objectlayer: { type: Object, default: null },
  requestOpenUUID: { type: String, default: null },
})
defineEmits(['navigate-to-widget'])

const store = useFontStore()
const fonts = computed(() => store.all)

// --- Search / sort ---

const search = ref('')
const sortKey = ref('name-asc')

const sortOptions = [
  { value: 'name-asc',  label: 'Name ↑' },
  { value: 'name-desc', label: 'Name ↓' },
]

const filteredFonts = computed(() => {
  let list = fonts.value
  const q = search.value.trim().toLowerCase()
  if (q) {
    list = list.filter(f => f.name.toLowerCase().includes(q) || (f.id ?? '').toLowerCase().includes(q))
  }
  list = [...list]
  switch (sortKey.value) {
    case 'name-asc':  list.sort((a, b) => a.name.localeCompare(b.name)); break
    case 'name-desc': list.sort((a, b) => b.name.localeCompare(a.name)); break
  }
  return list
})

function rowMeta(f) {
  const refs = props.objectlayer && f.file ? findWidgetsReferencing(props.objectlayer, 'font', f.file).length : 0
  const parts = []
  if (f.id) parts.push(f.id)
  parts.push(f.storageId != null ? (f.originalFilename ?? 'file uploaded') : 'no file uploaded')
  parts.push(`${refs} reference${refs === 1 ? '' : 's'}`)
  return parts
}

function getWarnings(f) {
  const warnings = []
  if (!f.id) warnings.push("No font id set — can't be exported until it has one.")
  const dup = store.all.find(other => other.uuid !== f.uuid && other.id === f.id && f.id)
  if (dup) warnings.push(`Font id "${f.id}" is also used by "${dup.name}".`)
  return warnings
}

function isUsed(f) {
  return props.objectlayer && f.file ? findWidgetsReferencing(props.objectlayer, 'font', f.file).length > 0 : false
}

// --- Editor popup ---

const editingUUID = ref(null)
const editingFont = computed(() => editingUUID.value ? store.getFont(editingUUID.value) : null)

watch(() => props.requestOpenUUID, (uuid) => {
  if (uuid) editingUUID.value = uuid
})

function createFont() {
  const uuid = store.addFont()
  editingUUID.value = uuid
}

function openEditor(uuid) {
  editingUUID.value = uuid
}

function duplicateFont(uuid) {
  const newUUID = store.duplicateFont(uuid)
  if (newUUID) editingUUID.value = newUUID
}

// --- Delete workflow ---

const pendingDeleteUUID = ref(null)
const pendingDeleteFont = computed(() => pendingDeleteUUID.value ? store.getFont(pendingDeleteUUID.value) : null)

function confirmDelete() {
  const uuid = pendingDeleteUUID.value
  pendingDeleteUUID.value = null
  if (editingUUID.value === uuid) editingUUID.value = null
  store.removeFont(uuid)
}
</script>
